// UPDATED FILE: pages/ApiTest.js
// This is the updated API test page to verify API calls in React.
// It includes the original buttons to fetch from JSONPlaceholder (public fake API).
// UPDATED: Added an input field for LPN value. Now you can enter any LPN (e.g., "RH01", "RH05") 
//      and click the test button to send POST to localhost:5269/api/GetLpnInfo
//      with header 'guid: ed0049f9-713b-4b9d-859e-899c3ead34a2' and dynamic body {"lpn": [input], "device": "app"}.
// Displays loading state, results, and error handling for all calls.
// No changes needed to myapp.js - the "/apitest" route is already there.

import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

function ApiTest() {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lpnInput, setLpnInput] = useState('RH05'); // Default LPN value

  // UPDATED: Function to test your specific POST API (uses dynamic LPN from input)
  const handleYourApiCall = async () => {
    if (!lpnInput.trim()) {
      setError('Please enter a valid LPN value.');
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);

    const apiUrl = 'http://localhost:5269/api/GetLpnInfo'; // Full URL with http://
    const headers = {
      'Content-Type': 'application/json',
      'guid': 'ed0049f9-713b-4b9d-859e-899c3ead34a2' // Custom header as specified
    };
    const body = {
      "lpn": lpnInput.trim(),
      "device": "app"
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} - ${response.statusText}`);
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('API Call Error:', err); // Log to console for debugging
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Original function for public API tests
  const handleApiCall = async (endpoint) => {
    setLoading(true);
    setError(null);
    setData(null);

    try {
      const response = await fetch(`https://jsonplaceholder.typicode.com/${endpoint}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (path) => {
    navigate(path);
  };

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>API Test - React API Function Test</h1>
      <p>Current Path: {location.pathname}</p>
      <p>This page confirms API calls work in React. Click buttons below to test fetches.</p>
      
      <div style={{ margin: '20px 0' }}>
        <h2>Your Custom API Test (POST to localhost:5269):</h2>
        <div style={{ marginBottom: '10px' }}>
          <label htmlFor="lpn-input" style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
            Enter LPN Value (e.g., RH01, RH05):
          </label>
          <input
            id="lpn-input"
            type="text"
            value={lpnInput}
            onChange={(e) => setLpnInput(e.target.value)}
            style={{
              padding: '8px',
              marginRight: '10px',
              border: '1px solid #ccc',
              borderRadius: '4px',
              width: '200px'
            }}
            placeholder="Enter LPN..."
          />
        </div>
        <button 
          onClick={handleYourApiCall} 
          disabled={loading}
          style={{ 
            margin: '5px', 
            padding: '10px', 
            backgroundColor: '#007bff', 
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Test POST /api/GetLpnInfo (with GUID Header & Dynamic Body - LPN: {lpnInput})
        </button>
        <br />
        <small style={{ color: 'gray' }}>
          Expects backend server running on localhost:5269. Check CORS if errors occur.
        </small>
      </div>

      <div style={{ margin: '20px 0' }}>
        <h2>Public API Tests (JSONPlaceholder):</h2>
        <button 
          onClick={() => handleApiCall('posts/1')} 
          disabled={loading}
          style={{ margin: '5px', padding: '10px' }}
        >
          Fetch Post #1 (GET /posts/1)
        </button>
        <br />
        <button 
          onClick={() => handleApiCall('users')} 
          disabled={loading}
          style={{ margin: '5px', padding: '10px' }}
        >
          Fetch All Users (GET /users)
        </button>
        <br />
        <button 
          onClick={() => handleApiCall('todos?userId=1&completed=true')} 
          disabled={loading}
          style={{ margin: '5px', padding: '10px' }}
        >
          Fetch Completed Todos for User 1 (GET /todos?...)
        </button>
        <br />
        <button 
          onClick={() => {
            setData(null);
            setError(null);
            setLoading(false);
          }} 
          style={{ margin: '5px', padding: '10px', backgroundColor: 'orange', color: 'white' }}
        >
          Clear Results
        </button>
      </div>

      <div style={{ margin: '20px 0' }}>
        <h3>API Response:</h3>
        {loading && <p style={{ color: 'blue' }}>Loading...</p>}
        {error && <p style={{ color: 'red' }}>Error: {error}</p>}
        {data && (
          <div style={{ border: '1px solid #ccc', padding: '10px', maxHeight: '300px', overflowY: 'auto' }}>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              {JSON.stringify(data, null, 2)}
            </pre>
          </div>
        )}
        {!loading && !error && !data && <p>No data fetched yet. Click a button to test.</p>}
      </div>

      <div style={{ margin: '20px 0' }}>
        <h2>Test Navigation Buttons (from RouterTest):</h2>
        <button onClick={() => handleNavigate('/Routertest')} style={{ margin: '5px', padding: '10px' }}>
          Go to Router Test
        </button>
        <br />
        <button onClick={() => handleNavigate('/')} style={{ margin: '5px', padding: '10px' }}>
          Go to Home (Protected - Login required)
        </button>
      </div>

      <div style={{ margin: '20px 0', border: '1px solid #ccc', padding: '10px' }}>
        <h3>Test React Rendering:</h3>
        <p>Static Text: This is rendered by React with state management (useState).</p>
        <ul>
          <li>Item 1: API test passed if you see JSON data after clicking a button.</li>
          <li>Item 2: Check for loading/error states. For localhost API, ensure server is running.</li>
          <li>Item 3: Open browser console (F12) for any fetch errors or network tab for requests.</li>
          <li>Item 4: Enter LPN in input and test - body updates dynamically!</li>
        </ul>
      </div>

      <button 
        onClick={() => alert('API Test Alert: React API function triggered!')} 
        style={{ margin: '5px', padding: '10px', backgroundColor: 'green', color: 'white' }}
      >
        Test Simple React Function (Alert)
      </button>
    </div>
  );
}

export default ApiTest;